/**
 * Dark-sky locations, read from the committed snapshot src/data/locations.json
 * (ERI-2594). Nothing here calls Supabase: the build renders every location
 * page from this file, so a database hiccup can't ship empty pages to search
 * engines. Refresh it with `npm run locations:snapshot` after an import.
 *
 * The snapshot is loaded with a dynamic import so it stays out of the main
 * bundle; in production the location routes get their data from the build's
 * loader manifest and the browser never downloads the whole file.
 */

import { findHub, hubPaths, type HubPage, type IndexedLocation } from '@/utils/locationHubs';

export interface Location {
  id: string;
  slug: string;
  name: string;
  country: string;
  state_province: string | null;
  type: string;
  latitude: number;
  longitude: number;
  bortle_class: number | null;
  area_hectares: number | null;
  recognition_year: number | null;
  recognizing_body: string | null;
  dsag_class: string | null;
  best_months: number[] | null;
  peak_core_altitude: number | null;
  darkness_hours_peak: number | null;
  /** Only on a location page's data (loadLocationPage); see locationDescriptions(). */
  description?: string | null;
  featured_image_url: string | null;
  is_verified: boolean;
  updated_at?: string;
}

/** What a location card shows (the list, the map and nearby locations). */
export type LocationSummary = Pick<
  Location,
  | 'id' | 'slug' | 'name' | 'country' | 'state_province' | 'type'
  | 'latitude' | 'longitude' | 'bortle_class' | 'best_months'
  | 'peak_core_altitude' | 'darkness_hours_peak' | 'featured_image_url'
>;

export type NearbyLocation = LocationSummary & { distance_miles: number };

async function snapshot(): Promise<Location[]> {
  const mod = await import('@/data/locations.json');
  return mod.default as unknown as Location[];
}

/**
 * The written descriptions, by slug (ERI-2853). They sit in their own file
 * because they are twice the size of everything else: the planner and the
 * list load the snapshot in the browser and never show a description.
 */
async function locationDescriptions(): Promise<Record<string, string>> {
  const mod = await import('@/data/locationDescriptions.json');
  return mod.default as Record<string, string>;
}

function toSummary(l: Location): LocationSummary {
  return {
    id: l.id, slug: l.slug, name: l.name, country: l.country, state_province: l.state_province,
    type: l.type, latitude: l.latitude, longitude: l.longitude, bortle_class: l.bortle_class,
    best_months: l.best_months, peak_core_altitude: l.peak_core_altitude,
    darkness_hours_peak: l.darkness_hours_peak, featured_image_url: l.featured_image_url,
  };
}

/**
 * Great-circle distance in miles on a 3959-mile Earth, as the database's
 * haversine_distance measured it (that one used the law of cosines; this form
 * gives the same distances without its rounding trouble at short range).
 */
export function haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** All slugs, for the build's getStaticPaths. */
export async function getAllLocationSlugs(): Promise<string[]> {
  return (await snapshot()).map((l) => l.slug);
}

/** All locations as cards, sorted by name, for the list page. */
export async function getAllLocations(): Promise<LocationSummary[]> {
  return (await snapshot()).map(toSummary);
}

/** One location by slug, or null. */
export async function getLocationBySlug(slug: string): Promise<Location | null> {
  return (await snapshot()).find((l) => l.slug === slug) ?? null;
}

/** Locations within `radiusMiles`, nearest first. */
export async function getNearbyLocations(
  lat: number,
  lon: number,
  radiusMiles = 200,
  excludeId?: string,
): Promise<NearbyLocation[]> {
  return (await snapshot())
    .filter((l) => l.id !== excludeId)
    .map((l) => ({ ...toSummary(l), distance_miles: haversineMiles(lat, lon, l.latitude, l.longitude) }))
    .filter((l) => l.distance_miles <= radiusMiles)
    .sort((a, b) => a.distance_miles - b.distance_miles);
}

/** What a location page needs, gathered once at build time by the route loader. */
export interface LocationPageData {
  location: Location | null;
  nearby: NearbyLocation[];
}

export async function loadLocationPage(slug: string | undefined): Promise<LocationPageData> {
  const found = slug ? await getLocationBySlug(slug) : null;
  if (!found) return { location: null, nearby: [] };
  const location = { ...found, description: (await locationDescriptions())[found.slug] ?? null };
  const nearby = (await getNearbyLocations(location.latitude, location.longitude, 200, location.id)).slice(0, 6);
  return { location, nearby };
}

function toIndexed(l: Location): IndexedLocation {
  return {
    slug: l.slug, name: l.name, country: l.country, state_province: l.state_province,
    type: l.type, bortle_class: l.bortle_class,
  };
}

/**
 * The country hubs (dark-sky-locations/in/spain) or the US state hubs
 * (dark-sky-locations/in/united-states/utah), for the build's getStaticPaths (ERI-2853).
 */
export async function getHubPaths(kind: 'country' | 'state'): Promise<string[]> {
  const depth = kind === 'country' ? 3 : 4;
  return hubPaths((await snapshot()).map(toIndexed))
    .map((path) => path.slice(1))
    .filter((path) => path.split('/').length === depth);
}

/** What a hub page needs, gathered at build time by its route loader. */
export interface LocationHubData {
  hub: HubPage | null;
}

export async function loadLocationHub(country: string | undefined, state?: string): Promise<LocationHubData> {
  return { hub: findHub((await snapshot()).map(toIndexed), country, state) };
}
